import React, { useState } from 'react';
import { useNotify, useDataProvider, useListContext } from 'react-admin';
import { Menu, MenuItem, CircularProgress, ButtonGroup, Button as MuiButton } from '@mui/material';
import { GetApp as DownloadIcon, ArrowDropDown } from '@mui/icons-material';

export const CustomExportButton = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const { filterValues, sort, total } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const handleMainButtonClick = () => {
    // Main button click exports all leads
    handleExport(undefined);
  };

  const handleArrowClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const EXPORT_CHUNK_SIZE = 10000;

  const handleExport = async (maxResults: number | undefined) => {
    handleClose();
    setLoading(true);

    try {
      let allData: any[] = [];

      if (maxResults !== undefined) {
        // Single request with limit (e.g. 100, 1000, 10000)
        const { data } = await dataProvider.getList('leads', {
          sort,
          filter: filterValues,
          pagination: { page: 1, perPage: maxResults },
          meta: { export: true, maxResults },
        });
        allData = data ?? [];
      } else {
        // Export all: fetch in chunks to avoid timeout / "failed to fetch" for large sets
        const totalToExport = total ?? 0;
        if (totalToExport === 0) {
          notify('No leads to export', { type: 'warning' });
          setLoading(false);
          return;
        }
        for (let offset = 0; offset < totalToExport; offset += EXPORT_CHUNK_SIZE) {
          const { data } = await dataProvider.getList('leads', {
            sort,
            filter: filterValues,
            pagination: { page: Math.floor(offset / EXPORT_CHUNK_SIZE) + 1, perPage: EXPORT_CHUNK_SIZE },
            meta: { export: true, maxResults: EXPORT_CHUNK_SIZE },
          });
          const chunk = data ?? [];
          allData = allData.concat(chunk);
          if (chunk.length < EXPORT_CHUNK_SIZE) break;
        }
        // Cap at list total so count/CSV never exceed it (avoids drift if backend returns extra rows)
        allData = allData.slice(0, totalToExport);
      }

      // Convert to CSV
      const csv = convertToCSV(allData);
      downloadCSV(csv, `leads-export-${getFilesystemSafeTimestamp()}.csv`);

      notify(`Exported ${allData.length} leads successfully`, { type: 'success' });
    } catch (error: any) {
      // Extract error message from response - handleError formats it as error.body or error.message
      const errorMessage =
        error?.body?.message ||
        error?.message ||
        error?.reason ||
        'Export failed. Please try again or apply additional filters.';
      notify(errorMessage, { type: 'error', autoHideDuration: 10000 });
      // Avoid logging full error objects that may include request details (PII).
      // Keep console output minimal and user-safe.
      // eslint-disable-next-line no-console
      console.error('Export leads error:', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ButtonGroup variant="text" sx={{ ml: 'auto' }}>
        <MuiButton
          onClick={handleMainButtonClick}
          disabled={loading || total === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
        >
          Export
        </MuiButton>
        <MuiButton
          onClick={handleArrowClick}
          disabled={loading || total === 0}
          sx={{ minWidth: 'auto', px: 0.5 }}
        >
          <ArrowDropDown />
        </MuiButton>
      </ButtonGroup>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleExport(100)}>
          Export Most Recent 100
        </MenuItem>
        <MenuItem onClick={() => handleExport(1000)}>
          Export Most Recent 1,000
        </MenuItem>
        <MenuItem onClick={() => handleExport(10000)}>
          Export Most Recent 10,000
        </MenuItem>
      </Menu>
    </>
  );
};

function convertToCSV(data: any[]): string {
  if (!data || data.length === 0) {
    return '';
  }

  // Get headers from first record
  const headers = Object.keys(data[0]);

  // Create CSV header row
  const csvHeader = headers.map(escapeCSVValue).join(',');

  // Create CSV data rows
  const csvRows = data.map(record =>
    headers.map(header => {
      const value = record[header];
      return escapeCSVValue(formatValue(value, header));
    }).join(',')
  );

  return [csvHeader, ...csvRows].join('\n');
}

function escapeCSVValue(value: string): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

function formatValue(value: any, fieldName?: string): string {
  if (value === null || value === undefined) {
    return '';
  }

  const normalizeYesNo = (boolValue: boolean): string => (boolValue ? 'yes' : 'no');

  const booleanFields = new Set<string>([
    // Common API response keys
    'isArchived',
    'smsOptIn',
    'emailOptIn',
    'errorFlag',
    // Defensive: accept snake_case variants if they ever appear
    'is_archived',
    'sms_opt_in',
    'email_opt_in',
    'error_flag',
  ]);

  const isBooleanField = Boolean(fieldName && booleanFields.has(fieldName));

  // Format dates nicely
  if (typeof value === 'number' && value > 1000000000000) {
    // Unix timestamp in milliseconds
    const date = new Date(value);
    return date.toISOString();
  }

  // Handle booleans (including integer 0/1 from backend)
  if (typeof value === 'boolean') {
    return normalizeYesNo(value);
  }

  // Normalize boolean-like values ONLY for known boolean fields.
  // This prevents corrupting free-form string fields (e.g., externalId/importBatchId) that may legitimately be "0"/"1"/"y"/"n".
  if (isBooleanField) {
    // Handle integer 0/1 as boolean values (backend sometimes returns opt-in fields as integers)
    if (typeof value === 'number' && (value === 0 || value === 1)) {
      return normalizeYesNo(value === 1);
    }

    // Handle string boolean-ish values (e.g., "0"/"1", "true"/"false", "yes"/"no").
    // IMPORTANT: Do NOT treat lifecycle status values (e.g., "archived"/"active") as boolean values.
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (
        normalized === '1' ||
        normalized === 'true' ||
        normalized === 'yes' ||
        normalized === 'y' ||
        normalized === 'on'
      ) {
        return 'yes';
      }
      if (
        normalized === '0' ||
        normalized === 'false' ||
        normalized === 'no' ||
        normalized === 'n' ||
        normalized === 'off'
      ) {
        return 'no';
      }
    }
  }

  // Handle objects and arrays
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Generate a filesystem-safe timestamp for filenames
 * Replaces colons and other invalid characters with hyphens
 * Format: YYYY-MM-DD_HH-MM-SS (e.g., 2026-01-20_14-30-45)
 */
function getFilesystemSafeTimestamp(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    // Create a link to the file
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export default CustomExportButton;
